import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  RestoreFromTrash,
  CheckCircle,
  GetApp
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const RecoveryPage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Handle recovery and import of missing orders
  const handleRecovery = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await axios.post('/api/recovery/find-missing-orders', {
        startDate,
        endDate
      });

      setResults(response.data);
      
      if (response.data.summary.total_imported > 0) {
        setError(`Recovery completed successfully: ${response.data.summary.total_imported} orders imported`);
      } else {
        setError('Recovery completed: No orders were imported (all missing orders were rejected by rules)');
      }
    } catch (err) {
      console.error('Recovery failed:', err);
      setError(err.response?.data?.message || 'Failed to recover missing orders');
    } finally {
      setLoading(false);
    }
  };

  // Export results to CSV
  const exportToCSV = () => {
    if (!results || !results.orders || results.orders.missing.length === 0) return;

    const importedOrders = results.orders.missing; // Now contains only imported orders
    const csvData = importedOrders.map(order => ({
      'Order Number': order.shopify_order_number,
      'Order ID': order.shopify_order_id,
      'Database ID': order.database_id || '',
      'Created At': new Date(order.created_at).toLocaleString(),
      'Imported At': new Date(order.imported_at).toLocaleString(),
      'Status': order.status || 'unknown',
      'Customer Name': order.customer_name || '',
      'Customer Email': order.customer_email || '',
      'Total Amount': order.total_amount || '',
      'Financial Status': order.financial_status || '',
      'Payment Method': order.payment_method || '',
      'Fulfillment Center': order.fulfillment_center_name || '',
      'FC Code': order.fulfillment_center_code || '',
      'Line Items': order.line_items_count || 0
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `imported-orders-${startDate}-to-${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Prepare data for DataGrid (now only shows imported orders)
  const getDataGridRows = () => {
    if (!results || !results.orders) return [];
    
    return results.orders.missing.map((order, index) => ({
      id: order.shopify_order_id || index,
      shopify_order_number: order.shopify_order_number,
      created_at: new Date(order.created_at).toLocaleString(),
      imported_at: new Date(order.imported_at).toLocaleString(),
      status: order.status,
      reason: order.reason,
      customer_name: order.customer_name || 'Unknown',
      total_amount: order.total_amount,
      customer_email: order.customer_email,
      financial_status: order.financial_status,
      line_items_count: order.line_items_count || 0,
      payment_method: order.payment_method || 'Unknown',
      fulfillment_center_name: order.fulfillment_center_name || '',
      fulfillment_center_code: order.fulfillment_center_code || '',
      database_id: order.database_id || ''
    }));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'imported': { color: 'success', icon: <CheckCircle />, label: 'Successfully Imported' }
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const columns = [
    {
      field: 'shopify_order_number',
      headerName: 'Order #',
      width: 100,
      renderCell: (params) => `#${params.value}`
    },
    {
      field: 'database_id',
      headerName: 'DB ID',
      width: 80
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160
    },
    {
      field: 'imported_at',
      headerName: 'Imported',
      width: 160
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 150
    },
    {
      field: 'total_amount',
      headerName: 'Amount',
      width: 100,
      renderCell: (params) => params.value ? `€${parseFloat(params.value).toFixed(2)}` : ''
    },
    {
      field: 'financial_status',
      headerName: 'Payment',
      width: 100
    },
    {
      field: 'line_items_count',
      headerName: 'Items',
      width: 80
    },
    {
      field: 'fulfillment_center_name',
      headerName: 'Fulfillment Center',
      width: 150
    },
    {
      field: 'payment_method',
      headerName: 'Payment Method',
      width: 120
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Order Recovery
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Find and automatically import missing orders from Shopify within a specific date range.
      </Typography>

      {/* Search Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              onClick={handleRecovery}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RestoreFromTrash />}
              fullWidth
              sx={{ height: '56px' }}
            >
              {loading ? 'Recovering Orders...' : 'Recover Missing Orders'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity={error.includes('successful') ? 'success' : 'error'} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      {results && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Shopify Orders Found
                </Typography>
                <Typography variant="h4">
                  {results.summary.total_shopify_orders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Missing Orders
                </Typography>
                <Typography variant="h4">
                  {results.summary.total_missing_orders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Successfully Imported
                </Typography>
                <Typography variant="h4" color="success.main">
                  {results.summary.total_imported}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Rejected by Rules
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {results.summary.total_rejected_by_rules + results.summary.total_rejected_by_cutoff}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Actions Bar */}
      {results && results.orders.missing.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={exportToCSV}
            startIcon={<GetApp />}
          >
            Export Imported Orders
          </Button>
        </Box>
      )}

      {/* Results Table */}
      {results && results.orders.missing.length > 0 && (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={getDataGridRows()}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
            }}
          />
        </Paper>
      )}

      {/* No Results */}
      {results && results.orders.missing.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Orders Imported
          </Typography>
          <Typography color="text.secondary">
            No missing orders were found that met the import criteria, or all orders in the selected date range are already in the database.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RecoveryPage;
